<template lang="html">
  <section v-if="user" :style="themableStyle" class="theme-color">
    <div class="container">
      <section class="hero is-white is-small has-bg-img">
        <!-- Hero body -->
        <div class="hero-body">
          <div class="columns">
            <!-- Hero Left -->
            <div class="column is-1 is-paddingless pt-3">
              <figure
                class="avatar-profile image"
                @mouseenter="displayAvatarEditionButton = true"
                @mouseleave="displayAvatarEditionButton = false"
              >
                <b-button
                  class="overlay"
                  size="is-small"
                  type="is-primary"
                  icon-left="pencil"
                  v-if="owner"
                  v-show="displayAvatarEditionButton"
                  @click="isAvatarUploadModalActive = true"
                ></b-button>
                <img style="border-radius: 50%" :src="user.avatar.url" alt="" />
              </figure>
            </div>

            <!-- Hero Center -->
            <div class="column is-paddingless pt-3 pl-3">
              <!-- Cosplayer name -->
              <p class="has-text-weight-semibold has-text-dark-primary is-size-3">
                {{ user.cosplayer_name }}
                <b-tooltip
                  :label="countryName"
                  position="is-right"
                  type="is-light-orange"
                >
                  <country-flag :country="user.country" :rounded="true" />
                </b-tooltip>
              </p>

              <!-- Icons -->
              <p
                v-if="
                  user.facebook ||
                  user.instagram ||
                  user.youtube ||
                  user.kofi ||
                  user.twitter
                "
                class="pl-2"
              >
                <b-tooltip label="Facebook page" position="is-bottom" type="is-primary"
                  ><a
                    v-if="user.facebook"
                    target="_blank"
                    :href="`https://facebook.com/${user.facebook}`"
                    ><b-icon type="is-dark" icon="facebook" size="is-small"></b-icon></a
                ></b-tooltip>
                <b-tooltip label="Instagram page" position="is-bottom" type="is-primary"
                  ><a
                    v-if="user.instagram"
                    target="_blank"
                    :href="`https://instagram.com/${user.instagram}`"
                    ><b-icon type="is-dark" icon="instagram" size="is-small"></b-icon></a
                ></b-tooltip>
                <b-tooltip label="Youtube page" position="is-bottom" type="is-primary"
                  ><a
                    v-if="user.youtube"
                    target="_blank"
                    :href="`https://youtube.com/channel/${user.youtube}`"
                    ><b-icon type="is-dark" icon="youtube" size="is-small"></b-icon></a
                ></b-tooltip>
                <b-tooltip label="Twitter page" position="is-bottom" type="is-primary"
                  ><a
                    v-if="user.twitter"
                    target="_blank"
                    :href="`https://twitter.com/${user.twitter}`"
                    ><b-icon type="is-dark" icon="twitter" size="is-small"></b-icon></a
                ></b-tooltip>
                <b-tooltip label="Ko-fi page" position="is-bottom" type="is-primary"
                  ><a
                    v-if="user.kofi"
                    target="_blank"
                    :href="`https://ko-fi.com/${user.kofi}`"
                    ><b-icon type="is-dark" icon="coffee" size="is-small"></b-icon></a
                ></b-tooltip>
              </p>

              <!-- Time  -->
              <span class="is-italic is-size-7 has-text-gray"
                >Joined {{ dateFromNow(user.created_at) }}</span
              >
            </div>

            <!-- Hero Right -->
            <div v-if="isLoggedIn" class="column is-narrow">
              <!-- Edit profile -->
              <span v-if="owner" class="p-1">
                <b-tooltip
                  label="Edit profile"
                  type="is-warning is-inverted"
                  position="is-left"
                >
                  <b-button
                    class="is-warning is-inverted"
                    @click="isUpdateProfileModalActive = true"
                  >
                    <b-icon icon="pencil"></b-icon> </b-button
                ></b-tooltip>
              </span>

              <!-- Add Teacher -->
              <add-teacher-button
                v-if="!owner"
                :key="user.username"
                :teacher="user"
                :user="currentUser.attributes.username"
              />

              <!-- Change theme -->
              <theme-changer
                v-if="owner"
                :theme="user.theme"
                :themable-id="user.username"
                :themable-type="`user`"
                @themeUpdated="user.theme.name = $event"
              />

              <!-- Manage images -->
              <image-management
                v-if="owner"
                :imageable-id="user.username"
                :imageable-type="'user'"
                @imagesUpdated="images = $event"
              />

              <!-- Reporting -->
              <report-button v-if="!owner" />
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <!-- Overview  -->
          <div class="box">
            <!-- Overview body -->
            <div class="columns is-centered is-vcentered is-marginless is-multiline">
              <!-- Left side -->
              <div class="column has-text-centered is-full">
                <div class="columns is-centered has-text-centered">
                  <div v-if="user.motto" class="column">
                    <attribute-title
                      :title="'Favorite citation'"
                      :tooltip="'Short sentence which represents the mindset of the user'"
                    />
                    <p
                      class="subtitle has-text-weight-semibold is-italic has-text-dark-primary"
                    >
                      <span>"{{ user.motto }}"</span>
                    </p>
                  </div>
                </div>

                <carousel-horizontal :images="images" />
              </div>
              <!-- Right side -->
              <div class="column is-8">
                <div class="columns is-vcentered is-centered">
                  <div v-if="user.configuration.birthday" class="column is-narrow">
                    <attribute-title :title="'Birthday'" />
                    {{ dayMonthDateFormat(user.birthday) }}
                  </div>
                  <div v-if="user.configuration.age" class="column is-narrow">
                    <attribute-title :title="'Age'" />
                    {{ user.age }}
                  </div>
                  <div
                    v-if="user.configuration.gender"
                    class="column has-text-centered is-narrow"
                  >
                    <attribute-title :title="'Gender'" />
                    <b-tooltip :label="textTitleize(user.gender)" position="is-right">
                      <gender-icon :gender="user.gender" />
                    </b-tooltip>
                  </div>
                  <div
                    v-if="user.language_list && user.language_list.length"
                    class="column is-narrow"
                  >
                    <attribute-title
                      :title="'Language(s)'"
                      :tooltip="'Languages spoke by the user'"
                    />
                    <tag-list :tags="user.language_list" :slice-index="3" />
                  </div>
                  <div v-if="user.skills && user.skills.length" class="column is-narrow">
                    <attribute-title
                      :title="'Skills'"
                      :tooltip="'Skills teached through the user Projects'"
                    />
                    <tag-list :tags="user.skills" :slice-index="3" />
                  </div>
                </div>
              </div>

              <div v-if="charactersList.length" class="column has-text-centered is-12">
                <attribute-title
                  :title="'Characters'"
                  :tooltip="'Characters linked to the Projects of the user'"
                />
                <carousel-characters :user="user" :charactersList="charactersList" />
              </div>
              <!-- <div class="column has-text-centered is-12">
                <p class="heading is-size-7 pb-2 pr-1 has-text-dark-orange">
                  Achievements
                </p>

                <b-tooltip type="is-dark" label="Challenges achieved by the user">
                  <b-icon
                    type="is-gray"
                    size="is-small"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
                <a>
                  <b-tooltip type="is-dark" label="Add an achievement">
                    <b-icon type="is-success" icon="plus"></b-icon>
                  </b-tooltip>
                </a>

                <div class="columns is-vcentered is-centered">
                  <div class="column has-text-right is-1">
                    <a>
                      <b-icon type="is-primary" icon="chevron-left"></b-icon>
                    </a>
                  </div>
                  <div class="column is-3">
                    <a>
                      <div class="box fullheightcustom is-secondary has-text-white">
                        <div class="columns is-multiline is-centered is-vcentered">
                          <div class="column is-4 has-text-weight-semibold">
                            <b-tooltip label="20/20/1994">
                              <b-icon icon="calendar-month"></b-icon> 2020<br />
                            </b-tooltip>
                          </div>

                          <div
                            class="column is-8 has-text-right is-italic is-paddingless pr-2"
                          >
                            <span class="ellipsis">
                              <b-icon icon="map-marker"></b-icon> World Cosplay Su m m m m
                              m m m m m m m m
                            </span>
                          </div>

                          <div class="column is-full">
                            <b-icon size="is-medium" icon="account"></b-icon>
                            <span class="ellipsis">
                              <span class="is-size-6 has-text-weight-semibold"
                                >World Cosplay Summit Belgian representative<br
                              /></span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>

                  <div class="column is-3">
                    <a>
                      <div class="box fullheightcustom is-secondary has-text-white">
                        <div class="columns is-multiline is-centered is-vcentered">
                          <div class="column is-4 has-text-weight-semibold">
                            <b-tooltip label="20/20/1994">
                              <b-icon icon="calendar-month"></b-icon> 2020<br />
                            </b-tooltip>
                          </div>

                          <div
                            class="column is-8 has-text-right is-italic is-paddingless pr-2"
                          >
                            <span class="ellipsis">
                              <b-icon icon="map-marker"></b-icon> World Cosplay Su m m m m
                              m m m m m m m m
                            </span>
                          </div>

                          <div class="column is-full">
                            <b-icon size="is-medium" icon="account"></b-icon>
                            <span class="ellipsis">
                              <span class="is-size-6 has-text-weight-semibold"
                                >World Cosplay Summit Belgian representative<br
                              /></span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                  <div class="column is-3">
                    <a>
                      <div class="box fullheightcustom is-secondary has-text-white">
                        <div class="columns is-multiline is-centered is-vcentered">
                          <div class="column is-4 has-text-weight-semibold">
                            <b-tooltip label="20/20/1994">
                              <b-icon icon="calendar-month"></b-icon> 2020<br />
                            </b-tooltip>
                          </div>

                          <div
                            class="column is-8 has-text-right is-italic is-paddingless pr-2"
                          >
                            <span class="ellipsis">
                              <b-icon icon="map-marker"></b-icon> World Cosplay Su m m m m
                              m m m m m m m m
                            </span>
                          </div>

                          <div class="column is-full">
                            <b-icon size="is-medium" icon="account"></b-icon>
                            <span class="ellipsis">
                              <span class="is-size-6 has-text-weight-semibold"
                                >World Cosplay Summit Belgian representative<br
                              /></span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>

                  <div class="column is-1">
                    <a>
                      <b-icon type="is-primary" icon="chevron-right"></b-icon>
                    </a>
                  </div>
                </div>
              </div> -->
              <div v-if="user.description" class="column has-text-centered is-11">
                <attribute-title :title="'Bio'" :tooltip="'User short auto-biography'" />
                <p>{{ user.description }}</p>
                <!-- Add toggle more content -->
              </div>
            </div>
          </div>

          <b-tabs id="projects" type="is-toggle" expanded>
            <!-- Projects -->
            <b-tab-item v-if="projects">
              <template slot="header">
                <b-icon icon="folder-multiple-outline"></b-icon>
                <span class="has-text-weight-semibold">
                  Projects <b-tag rounded> {{ projects.length }} </b-tag></span
                >
              </template>
              <div class="container">
                <h1 class="title is-size-4 has-text-dark-primary">
                  Projects
                  <b-tooltip
                    v-if="owner"
                    position="is-right"
                    type="is-success"
                    label="Add new Project"
                  >
                    <a @click="isNewProjectModalActive = true">
                      <b-icon
                        size="is-small"
                        type="is-success"
                        icon="plus-thick"
                      ></b-icon>
                    </a>
                  </b-tooltip>
                </h1>
                <h2 class="subtitle is-italic is-size-6">
                  Creations from {{ user.cosplayer_name }} containing Crafts, Purchases
                  and Q&A
                </h2>
                <div class="is-italic has-text-gray" v-if="!projects.length">
                  There is no project yet.
                </div>
                <div v-else class="columns is-multiline">
                  <!-- Projects -->
                  <div
                    class="column is-one-fifth"
                    v-for="project in projects"
                    :key="project.id"
                  >
                    <project-card :project="project" />
                  </div>
                </div>
              </div>
            </b-tab-item>
            <!-- Articles -->
            <b-tab-item v-if="articles">
              <template slot="header">
                <b-icon icon="newspaper-variant-outline"></b-icon>
                <span class="has-text-weight-semibold">
                  Articles <b-tag rounded> {{ articles.length }} </b-tag></span
                >
              </template>
              <div class="container">
                <h1 class="title is-size-4">
                  Articles
                  <b-tooltip
                    v-if="owner"
                    position="is-right"
                    type="is-success"
                    label="Add new Article"
                  >
                    <a @click="isNewArticleModalActive = true">
                      <b-icon
                        size="is-small"
                        type="is-success"
                        icon="plus-thick"
                      ></b-icon>
                    </a>
                  </b-tooltip>
                </h1>
                <h2 class="subtitle is-italic is-size-6">
                  Compositions written by {{ user.cosplayer_name }} about a great variety
                  of Topics
                </h2>
                <div class="is-italic has-text-gray" v-if="!articles.length">
                  There is no article yet.
                </div>

                <div v-else class="columns is-multiline">
                  <div
                    class="column is-one-fifth"
                    v-for="article in articles"
                    :key="article.id"
                  >
                    <article-card :article="article" />
                  </div>
                </div>
              </div>
            </b-tab-item>

            <!-- Collections -->
            <b-tab-item v-if="collections">
              <template slot="header">
                <b-icon icon="treasure-chest"></b-icon>
                <span class="has-text-weight-semibold"
                  >Collections <b-tag rounded> {{ collections.length }} </b-tag></span
                >
              </template>
              <div class="container">
                <h1 class="title is-size-4">
                  Collections
                  <b-tooltip
                    v-if="owner"
                    position="is-right"
                    type="is-success"
                    label="Manage Collections"
                  >
                    <router-link
                      :to="{
                        path: `/users/${currentUser.attributes.username}/collections`,
                      }"
                    >
                      <b-icon size="is-small" type="is-success" icon="cogs"></b-icon>
                    </router-link>
                  </b-tooltip>
                </h1>
                <h2 class="subtitle is-italic is-size-6">
                  Set of Projects, Crafts and Articles saved by
                  {{ user.cosplayer_name }}
                </h2>
                <div class="is-italic has-text-gray" v-if="!collections.length">
                  There is no collection yet.
                </div>
                <div v-else class="columns is-multiline">
                  <!-- Collections -->
                  <div
                    class="column is-one-fifth"
                    v-for="collection in collections"
                    :key="collection.id"
                  >
                    <collection-card :collection="collection" :username="user.username" />
                  </div>
                </div>
              </div>
            </b-tab-item>

            <!-- Teachers -->
            <b-tab-item v-if="teachers">
              <template slot="header">
                <b-icon icon="teach"></b-icon>
                <span> Teachers</span>
              </template>
              <h1 class="title is-size-4">Teachers</h1>
              <h2 class="subtitle is-italic is-size-6">
                Teachers that {{ user.cosplayer_name }} follows in order to learn more
                from them
              </h2>
              <div class="is-italic has-text-gray" v-if="!teachers.length">
                This user doesn't follow any teacher yet.
              </div>
              <div v-else class="columns is-multiline">
                <div
                  v-for="(teacher, index) in teachers"
                  :key="index"
                  class="column is-1 student-card"
                >
                  <div class="card is-shadowless has-text-centered">
                    <div class="card-image">
                      <router-link
                        :to="{ path: `/users/${teacher.attributes.username}` }"
                      >
                        <figure class="image is-1by1">
                          <img
                            style="border-radius: 50%"
                            :src="teacher.attributes.avatar.url"
                            alt="Placeholder image"
                          /></figure
                      ></router-link>
                    </div>
                    <div>
                      <small>
                        <router-link
                          class="has-text-dark-primary"
                          :to="{ path: `/users/${teacher.attributes.username}` }"
                          >{{ teacher.attributes.cosplayer_name }}</router-link
                        >
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab-item>
            <!-- Students -->
            <b-tab-item v-if="students">
              <template slot="header">
                <b-icon icon="glasses"></b-icon>
                <span> Students</span>
              </template>
              <h1 class="title is-size-4">Students</h1>
              <h2 class="subtitle is-italic is-size-6">
                Students who learn from {{ user.cosplayer_name }}
              </h2>
              <div class="is-italic has-text-gray" v-if="!students.length">
                This user isn't followed by any student yet.
              </div>
              <div class="columns is-multiline">
                <div
                  v-for="(student, index) in students"
                  :key="index"
                  class="column is-1 student-card"
                >
                  <div class="card is-shadowless has-text-centered">
                    <div class="card-image">
                      <router-link
                        :to="{ path: `/users/${student.attributes.username}` }"
                      >
                        <figure class="image is-1by1">
                          <img
                            style="border-radius: 50%"
                            :src="student.attributes.avatar.url"
                            alt="Placeholder image"
                          />
                        </figure>
                      </router-link>
                    </div>
                    <div>
                      <small>
                        <router-link
                          class="has-text-dark-primary"
                          :to="{ path: `/users/${student.attributes.username}` }"
                          >{{ student.attributes.cosplayer_name }}</router-link
                        >
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab-item>
          </b-tabs>

          <!-- Comments -->
          <comments
            :store-action-root="'users/comments'"
            :action-parameter="$route.params.username"
            :commentable="'user'"
            :commentable-owner="owner"
            :user-id="user.id"
            :key="$route.params.username"
            :theme="user.theme"
          />
        </div>
      </section>

      <b-modal
        :active.sync="isNewProjectModalActive"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <new-project />
      </b-modal>

      <b-modal
        :active.sync="isNewArticleModalActive"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <new-article />
      </b-modal>

      <b-modal
        :active.sync="isAvatarUploadModalActive"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <image-uploader
          :emit-signal="'avatarChanged'"
          :title="'avatar'"
          @avatarChanged="updateAvatar"
        />
      </b-modal>

      <b-modal
        :active.sync="isUpdateProfileModalActive"
        :can-cancel="modalCancellable"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <update-profile-modal @userUpdated="updateUser" />
      </b-modal>

      <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>
    </div>
  </section>
</template>

<script>
// Libraries
import axios from 'axios'
import { mapGetters } from 'vuex'
import CountryFlag from 'vue-country-flag'
import VueScrollTo from 'vue-scrollto'

// Components
import NewProject from '@/views/Profile/NewProject'
import NewArticle from './NewArticle'
import ImageUploader from '@/components/modals/ImageUploader'
import AddTeacherButton from '@/components/buttons/AddTeacherButton'
import CarouselHorizontal from '@/components/concerns/Images/CarouselHorizontal'
import GenderIcon from '@/components/GenderIcon'
import Comments from '@/components/concerns/Comments'
import ThemeChanger from '@/components/buttons/ThemeChanger'
import ImageManagement from '@/components/concerns/Images/ImageManagement'
import UpdateProfileModal from '@/views/Profile/UpdateProfileModal'
import CarouselCharacters from './CarouselCharacters'
import AttributeTitle from '@/components/Attributes/AttributeTitle'

// Mixins
import { dateMixin } from '@/mixins/dateMixin.js'
import { backendMixin } from '@/mixins/backendMixin.js'
import { themableMixin } from '@/mixins/themableMixin'
import { notificationMixin } from '@/mixins/notificationMixin'
import TagList from '@/components/concerns/Tags/TagList'
import { textMixin } from '@/mixins/textMixin'
import ProjectCard from '@/views/Profile/Cards/ProjectCard'
import ArticleCard from '@/views/Profile/Cards/ArticleCard'
import CollectionCard from '@/views/Profile/Cards/CollectionCard'
import ReportButton from '@/components/buttons/ReportButton'

export default {
  mixins: [dateMixin, backendMixin, themableMixin, textMixin, notificationMixin],
  data () {
    return {
      user: null,
      projects: null,
      articles: null,
      students: null,
      teachers: null,
      collections: [],
      images: [],
      countryName: null,
      isNewProjectModalActive: false,
      isNewArticleModalActive: false,
      isAvatarUploadModalActive: false,
      displayAvatarEditionButton: false,
      isLoading: false,
      isUpdateProfileModalActive: false,
      modalCancellable: [true, true, false]
    }
  },
  components: {
    ReportButton,
    CollectionCard,
    ArticleCard,
    ProjectCard,
    ImageUploader,
    TagList,
    CountryFlag,
    NewProject,
    NewArticle,
    GenderIcon,
    AddTeacherButton,
    Comments,
    CarouselHorizontal,
    ThemeChanger,
    ImageManagement,
    UpdateProfileModal,
    CarouselCharacters,
    AttributeTitle
  },
  computed: {
    ...mapGetters({
      isOwner: 'isOwner',
      isLoggedIn: 'isLoggedIn',
      currentUser: 'currentUser'
    }),
    owner () {
      return this.isOwner(this.user.id)
    },
    themableTheme () {
      return this.user ? this.user.theme.name : null
    },
    charactersList () {
      if (this.projects) {
        return this.projects.filter((project) => {
          return (
            project.attributes.character ||
            project.attributes.version ||
            project.attributes.universe
          )
        })
      } else return []
    }
  },
  created () {
    this.fetchUser()
  },
  watch: {
    $route: 'fetchUser'
  },
  methods: {
    updateUser (values) {
      this.user = values
    },
    fetchUser () {
      this.isLoading = true
      this.$store
        .dispatch('users/show', this.$route.params.username)
        .then((resp) => {
          this.user = resp.data.data.attributes
          this.setCountryName()
          this.fetchUserData()
          this.isLoading = false
        })
        .catch((error) => this.handleError(error, this.$route))
    },
    updateAvatar (value) {
      this.isLoading = true
      this.$store
        .dispatch('users/update', { avatar: value })
        .then((resp) => {
          this.user = resp.data.data.attributes
          this.isLoading = false
        })
        .catch((error) => {
          console.log(error.response)
          this.isLoading = false
          this.alertError()
        })
    },
    fetchUserData () {
      this.$store
        .dispatch('users/projects/index', this.$route.params.username)
        .then((resp) => {
          this.projects = resp.data.data
        })
        .then((_) => {})
      this.$store
        .dispatch('images/index', {
          imageableType: 'user',
          imageableId: this.user.username
        })
        .then((resp) => {
          this.images = resp.data.data
        })
        .catch((error) => {
          console.log('error')
          console.log(error.response)
        })
      this.$store
        .dispatch('users/articles/index', this.$route.params.username)
        .then((resp) => {
          this.articles = resp.data.data
        })
        .then((_) => {})
      this.$store
        .dispatch('users/students/index', this.$route.params.username)
        .then((resp) => {
          this.students = resp.data.data
        })
        .then((_) => {})
      this.$store
        .dispatch('users/teachers/index', this.$route.params.username)
        .then((resp) => {
          this.teachers = resp.data.data
        })
        .then((_) => {})
      this.$store
        .dispatch('users/collections/index', this.$route.params.username)
        .then((resp) => {
          this.collections = resp.data.data
        })
        .catch((_) => {})
    },
    setCountryName () {
      axios
        .get(`https://restcountries.eu/rest/v1/alpha/${this.user.country}`, {
          transformRequest: [
            (data, headers) => {
              delete headers.common.Authorization
              return data
            }
          ]
        })
        .then((res) => {
          this.countryName = res.data.name
        })
    }
  },
  mounted () {
    let self = this
    setTimeout(function () {
      if (self.$router.currentRoute['hash']) {
        VueScrollTo.scrollTo(self.$router.currentRoute['hash'], 1)
      }
    }, 500)
  }
}
</script>

<style lang="scss" scoped></style>
